import { call } from "@/firebase";
import { useUserStore } from "@/store/user";
import { decodeAuthClaims } from "@aircap/common/auth";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import type { Auth } from "firebase/auth";
import { AuthErrorCodes, signInWithCustomToken } from "firebase/auth";
import { jwtDecode } from "jwt-decode";

export interface TokenAuthResult {
  success: boolean;
  tokenExpired?: boolean;
  error?: unknown;
}

export async function tokenAuth(auth: Auth, token: string): Promise<TokenAuthResult> {
  try {
    console.log("Logging in with custom token from URL param");
    const credential = await signInWithCustomToken(auth, token);
    await useUserStore().authenticate(credential.user);
    return { success: true };
  } catch (cause) {
    if (cause instanceof FirebaseError && cause.code === AuthErrorCodes.INVALID_CUSTOM_TOKEN) {
      console.log("Token is expired, attempting to resend invite");
      try {
        const decodedToken = jwtDecode<{ uid: string; claims: Record<string, unknown> }>(token);
        // Emails sent before the account rename hold tokens naming it `projectId`: https://github.com/aircap-tech/aircap-app/issues/1285
        const { projectId, ...rawClaims } = decodedToken.claims;
        const claims = decodeAuthClaims({ accountId: projectId, ...rawClaims });
        if (claims.kind !== "operator")
          throw new Error(`Unexpected ${claims.kind} token`, { cause });
        await call("sendOperatorConnectLinkEmail")({
          accountId: claims.accountId,
          contactId: decodedToken.uid.slice(8),
        });
        console.log("Invite resent successfully");
        return { success: false, tokenExpired: true };
      } catch (resendCause) {
        const error = new Error("Error resending invite", { cause: resendCause });
        console.error(error);
        captureException(error);
        return { success: false, error };
      }
    }

    const error = new Error("Error signing in with custom token", { cause });
    console.error(error);
    captureException(error);
    return { success: false, error };
  }
}
